module Client.Page.Admin.UserList

open System
open System.Collections.Generic
open Client.Components
open Client.Domain
open Client.Infrastructure
open Client.InfrastructureTypes
open Elmish
open Fable.FontAwesome
open Fulma
open Shared
open Client
open Fable.React
open Client.Infrastructure.Api
open Client.DomainTypes.Msg
open Shared.Dto.Dto
open Shared.Dto.Page
open Shared.Dto.Page.UserList
open Shared.Dto.User
open Shared.Infrastructure
open Thoth.Elmish

type private DeletionModal = {
    User: string
    Id: int
    IsRunning: bool
}

type private IsBlockedModal = {
    User: string
    Request: UpdateUserIsBlockedRequest
    IsRunning: bool
}

type ModalData =
    private
    | EditModal of Forms.User.Model
    | AddPackageModal of Forms.AddPackage.Model
    | DeletionModal of DeletionModal
    | BlockingModal of IsBlockedModal

type Data = {
    AllUsers: IdValue<FullUserDto> list
    DisplayedUsers: IdValue<FullUserDto> list
    Packages: IdValue<Package> list
    Session: UserSession
    Modal: ModalData option
    DisplayFilters: bool
    NameFilter: string option
    MailFilter: string option
    CustomerNumberFilter: string option
}

type Loading = {
    Session: UserSession
    Users: IdValue<FullUserDto> list option
    Packages: IdValue<Package> list option
}

type Model = Loadable<Data, Loading>

let init (userSession: UserSession) =
    let request = {
        SessionKey = userSession.SessionKey
        Data = ()
    }

    let cmd =
        Cmd.batch [
            Cmd.OfAsync.perform api.getAllFullUsers request (UserListMsg.UsersLoaded >> UserList)
            Cmd.OfAsync.perform api.getAllPackages request (UserListMsg.PackagesLoaded >> UserList)
        ]

    let loadingModel = {
        Session = userSession
        Users = None
        Packages = None
    }

    Loadable.Loading loadingModel, cmd

let createDataModel (loadingModel: Loading) : Data option =
    Option.map2
        (fun users packages -> {
            AllUsers = users
            DisplayedUsers = users
            Packages = packages
            Session = loadingModel.Session
            Modal = None
            DisplayFilters = false
            NameFilter = None
            MailFilter = None
            CustomerNumberFilter = None
        })
        loadingModel.Users
        loadingModel.Packages

let getModel (loadingModel: Loading) : Model =
    createDataModel loadingModel
    |> Option.map Data
    |> Option.defaultValue (Loading loadingModel)

let private filterUser (model: Data) (user: IdValue<FullUserDto>) : bool =
    let nameFilterMatches =
        match model.NameFilter with
        | Some filter -> String.containsIgnoreCase (UserList.getFullName user.Value) filter
        | None -> true

    let mailFilterMatches =
        match model.MailFilter with
        | Some filter -> String.containsIgnoreCase (UserList.getMail user.Value) filter
        | None -> true

    let customerNumberFilterMatches =
        match model.CustomerNumberFilter, UserList.getCustomerNumber user.Value with
        | Some filter, Some customerNumber -> String.containsIgnoreCase customerNumber filter
        | Some _, None -> false
        | None, _ -> true

    nameFilterMatches && mailFilterMatches && customerNumberFilterMatches

let private refreshDisplayedUsers (model: Data) : Data = {
    model with
        DisplayedUsers = model.AllUsers |> List.filter (filterUser model)
}

let handleUserIsBlockedUpdated (result: UserIsBlockedResult) (data: Data) : Data * Cmd<Msg> =
    match result.Response with
    | Ok _ ->
        let message =
            match result.IsBlocked with
            | true -> sprintf "Der Benutzer '%s' wurde gesperrt" result.UserName
            | false -> sprintf "Der Benutzer '%s' wurde freigeschaltet" result.UserName

        let toast = message |> Toast.create |> Toast.success

        let updatedUsers =
            data.AllUsers
            |> List.map (fun user ->
                let mapper =
                    if user.Id = result.UserId then
                        IdValue.map (updateIsBlockedStatus result.IsBlocked)
                    else
                        id

                mapper user
            )

        let newData =
            {
                data with
                    AllUsers = updatedUsers
                    Modal = None
            }
            |> refreshDisplayedUsers

        newData, toast
    | Result.Error _ -> data, Cmd.none

let private deletionFailedToast (result: UserDeletedResult) : Cmd<Msg> =
    let message = sprintf "Fehler beim Löschen des Benutzers '%s'" result.UserName

    message |> Toast.create |> Toast.error

let update (msg: UserListMsg) (model: Model) =
    match msg, model with
    | UsersLoaded usersResponse, Loadable.Loading loading ->
        match usersResponse with
        | Result.Ok userList ->
            let newLoadingModel = { loading with Users = Some userList }

            getModel newLoadingModel, Cmd.none
        | Result.Error message ->
            let errorMessage =
                match message with
                | AuthErr Unauthorized -> "Sie dürfen die notwendigen Daten nicht laden"
                | AuthErr Unauthenticated -> "Sie sind nicht eingeloggt. Bitte laden Sie die Seite neu"
                | CustomErr error -> error

            let cmd = Toast.create errorMessage |> Toast.error

            model, cmd
    | PackagesLoaded packagesResponse, Loadable.Loading loading ->
        match packagesResponse with
        | Result.Ok packages ->
            let newLoadingModel = { loading with Packages = Some packages }

            getModel newLoadingModel, Cmd.none
        | Result.Error message ->
            let errorMessage =
                match message with
                | AuthErr Unauthorized -> "Sie dürfen die notwendigen Daten nicht laden"
                | AuthErr Unauthenticated -> "Sie sind nicht eingeloggt. Bitte laden Sie die Seite neu"
                | CustomErr error -> error

            let cmd = Toast.create errorMessage |> Toast.error

            model, cmd
    | OpenEditModal user, Loadable.Data data ->
        let modal = Forms.User.init data.Packages user |> EditModal

        Loadable.Data { data with Modal = Some modal }, Cmd.none
    | UserListMsg.FormMsg formMsg, Loadable.Data data ->
        match data.Modal with
        | Some(EditModal modal) ->
            let modal, cmd, formResult = Forms.User.update formMsg modal

            match formResult with
            | Forms.User.FormResult.Noop -> Loadable.Data { data with Modal = Some(EditModal modal) }, cmd
            | Forms.User.FormResult.CloseModal -> Loadable.Data { data with Modal = None }, cmd
            | Forms.User.FormResult.CloseAndRefresh -> init data.Session |> Cmds.batch cmd
        | _ -> model, Cmd.none
    | OpenAddPackageModal userId, Loadable.Data data ->
        Loadable.Data {
            data with
                Modal =
                    Forms.AddPackage.init userId data.Packages
                    |> Loadable.Data
                    |> AddPackageModal
                    |> Some
        },
        Cmd.none
    | AddPackage addPackageMsg, Loadable.Data data ->
        match data.Modal with
        | Some(AddPackageModal modal) ->
            let modal, cmd, formResult = Forms.AddPackage.update addPackageMsg modal

            match formResult with
            | Forms.AddPackage.FormResult.Noop -> Loadable.Data { data with Modal = Some(AddPackageModal modal) }, cmd
            | Forms.AddPackage.FormResult.CloseModal -> Loadable.Data { data with Modal = None }, cmd
            | Forms.AddPackage.FormResult.CloseAndRefresh -> init data.Session |> Cmds.batch cmd
        | _ -> model, Cmd.none
    | DeleteUser(id, name), Loadable.Data data ->
        let request = {
            SessionKey = data.Session.SessionKey
            Data = id
        }

        let cmd =
            let msgFactory =
                fun response -> {
                    UserId = id
                    UserName = name
                    Response = response
                }

            Cmd.OfAsync.perform api.deleteUser request (msgFactory >> UserDeleted >> UserList)

        model, cmd
    | UserDeleted result, Loadable.Data data ->
        match result.Response with
        | Ok success ->
            if success then
                let toastCmd = Toast.create "Benutzer wurde erfolgreich gelöscht" |> Toast.success

                let filteredUsers =
                    data.AllUsers |> List.filter (fun user -> user.Id <> result.UserId)

                let newData = { data with AllUsers = filteredUsers } |> refreshDisplayedUsers

                Loadable.Data newData, toastCmd
            else
                model, deletionFailedToast result
        | Result.Error _ -> model, deletionFailedToast result
    | OpenUpdateUserIsBlockedConfirmation(fullName, request), Loadable.Data data ->
        let modal =
            {
                User = fullName
                Request = request
                IsRunning = false
            }
            |> BlockingModal
            |> Some

        Loadable.Data { data with Modal = modal }, Cmd.none
    | UpdateUserIsBlocked(name, data), Data dataModel ->
        let request = {
            SessionKey = dataModel.Session.SessionKey
            Data = data
        }

        let createMessageResult =
            fun response -> {
                UserId = data.UserId
                UserName = name
                IsBlocked = data.IsBlocked
                Response = response
            }

        model,
        Cmd.OfAsync.perform api.setUserIsBlocked request (createMessageResult >> UserIsBlockedUpdated >> UserList)
    | UserIsBlockedUpdated result, Loadable.Data data ->
        handleUserIsBlockedUpdated result data |> Tuple.mapFirst Loadable.Data
    | UserListMsg.CloseModal, Loadable.Data data -> Loadable.Data { data with Modal = None }, Cmd.none
    | UserListMsg.ToggleFilterDisplay, Loadable.Data data ->
        Loadable.Data { data with DisplayFilters = not data.DisplayFilters }, Cmd.none
    | UserListMsg.NameFilterChanged name, Loadable.Data data ->
        let newData = { data with NameFilter = name } |> refreshDisplayedUsers

        Loadable.Data newData, Cmd.none
    | UserListMsg.MailFilterChanged mail, Loadable.Data data ->
        let newData = { data with MailFilter = mail } |> refreshDisplayedUsers

        Loadable.Data newData, Cmd.none
    | UserListMsg.CustomerNumberFilterChanged customerNumber, Loadable.Data data ->
        let newData =
            { data with CustomerNumberFilter = customerNumber } |> refreshDisplayedUsers

        Loadable.Data newData, Cmd.none
    | UserListMsg.ImpersonateUser userId, Loadable.Data data ->
        let request = {
            SessionKey = data.Session.SessionKey
            Data = userId
        }

        let cmd =
            Cmd.OfAsync.perform api.impersonateUser request (GlobalMsg.ImpersonationResult >> Global)

        Loadable.Data data, cmd
    | _, _ -> model, Cmd.none

let createNewUserButton dispatch =
    Button.button [
        Button.Color IsLink
        Button.OnClick(fun _ -> dispatch (UserList <| UserListMsg.OpenEditModal None))
    ] [ str "Neuen Benutzer erstellen" ]

let private addValue<'T> (item: 'T) (dict: Dictionary<'T, int>) =
    match Dict.tryGetValue dict item with
    | Some existing -> dict[item] <- existing + 1
    | None -> dict[item] <- 1

    dict

let private countedValues<'T when 'T: equality> (list: 'T list) : Dictionary<'T, int> =
    list |> List.fold (fun dict item -> addValue item dict) (Dictionary())

let private getRoleDataString (packages: IdValue<Package> list) (user: FullUserDto) : string =
    match user with
    | FullUserDto.Admin _ -> ""
    | FullUserDto.RegularUser _ ->
        user
        |> getPackages
        |> List.map (fun purchased ->
            packages
            |> List.tryFind (fun p -> p.Id = purchased.Value.PackageId)
            |> Option.map (fun p -> p.Value.Code)
        )
        |> List.choose id
        |> countedValues
        |> Seq.sortBy (fun i -> i.Key)
        |> Seq.map (fun item ->
            match item.Value with
            | 1 -> item.Key
            | _ -> sprintf "%dx %s" item.Value item.Key
        )
        |> fun chars -> String.Join(", ", chars)
        |> sprintf "Pakete: %s"
    | FullUserDto.TestUser testUser -> testUser.ValidUntil |> DateTime.toShortString |> sprintf "Gültig bis: %s"

let private userIsDisabled (user: FullUserDto) : bool =
    match user with
    | FullUserDto.Admin _
    | FullUserDto.RegularUser _ -> false
    | FullUserDto.TestUser testUser -> if testUser.ValidUntil > DateTime.Now then false else true

let private createDeleteDropDownItem dispatch (user: IdValue<FullUserDto>) =
    let f =
        fun _ ->
            let data = (user.Id, Shared.Dto.Page.UserList.getFullName user.Value)

            dispatch (data |> UserListMsg.DeleteUser |> UserList)

    DropDownList.createItem Fa.Solid.Trash "Löschen" f

let private createBlockUserDropDownItem dispatch (user: IdValue<FullUserDto>) =
    match user.Value with
    | FullUserDto.Admin _
    | FullUserDto.TestUser _ -> None
    | FullUserDto.RegularUser regularUser ->

        let fullName = Shared.Dto.Page.UserList.getFullName user.Value

        let label, icon, isBlocked =
            if regularUser.IsBlocked then
                "Freischalten", Fa.Solid.Check, false
            else
                "Sperren", Fa.Solid.Ban, true

        let request: UpdateUserIsBlockedRequest = {
            UserId = user.Id
            IsBlocked = isBlocked
        }

        let msgData = fullName, request

        DropDownList.createItem
            icon
            label
            (fun _ -> dispatch (UserListMsg.OpenUpdateUserIsBlockedConfirmation msgData |> UserList))
        |> Some

let private createDropdownMenu (dispatch: Msg -> unit) (user: IdValue<FullUserDto>) =
    let dropDownItems = [
        DropDownList.createItem
            Fa.Solid.Plus
            "Paket hinzufügen"
            (fun _ -> dispatch (UserListMsg.OpenAddPackageModal user.Id |> UserList))
        DropDownList.createItem
            Fa.Solid.Edit
            "Bearbeiten"
            (fun _ -> dispatch (UserListMsg.OpenEditModal(Some user) |> UserList))

        DropDownList.createItem
            Fa.Solid.UserFriends
            "Einloggen als"
            (fun _ -> dispatch (UserListMsg.ImpersonateUser(user.Id) |> UserList))

        createDeleteDropDownItem dispatch user

        match createBlockUserDropDownItem dispatch user with
        | Some item -> item
        | None -> ()
    ]

    DropDownList.createContextMenu dropDownItems

let userToRow dispatch (packages: IdValue<Package> list) (index: int) (user: IdValue<FullUserDto>) =
    let customerNumber =
        match user.Value with
        | FullUserDto.RegularUser regular -> regular.CustomerNumber
        | _ -> None

    tr [
        classList [ ("user_disabled", userIsDisabled user.Value) ]
    ] [
        td [] [ Table.rowIndexString index ]
        td [] [
            str (Shared.Dto.Page.UserList.getFullName user.Value)
        ]
        td [] [ str (getMail user.Value) ]
        td [] [ str (userTypeToString user.Value) ]
        td [] [
            str (
                getLastActivity user.Value
                |> Option.map (fun dt -> dt.LocalDateTime)
                |> Option.map DateTime.toDateTimeString
                |> Option.defaultValue "Noch nie"
            )
        ]
        td [] [
            user.Value |> getRoleDataString packages |> str
        ]
        td [] [
            customerNumber |> Option.defaultValue "" |> str
        ]
        td [] [ createDropdownMenu dispatch user ]
    ]

let userListToTable dispatch (packages: IdValue<Package> list) (users: IdValue<FullUserDto> list) =
    Table.table [
        Table.IsBordered
        Table.IsFullWidth
        Table.IsStriped
    ] [
        thead [] [
            tr [] [
                th [] [ str "#" ]
                th [] [ str "Name" ]
                th [] [ str "Email Adresse" ]
                th [] [ str "Benutzer Typ" ]
                th [] [ str "Letzte Aktivität" ]
                th [] [ str "Rolle-Daten" ]
                th [] [ str "SevDesk Kundennr." ]
                th [] []
            ]
        ]
        tbody [] (List.mapi (userToRow dispatch packages) users)
    ]

let private toggleFilterDisplayButton dispatch =
    let customOptions = [
        CommonButtons.onClick (fun _ -> dispatch (UserList UserListMsg.ToggleFilterDisplay))
    ]

    CommonButtons.createFilterButton customOptions

let tableHeader dispatch =
    Level.level [] [
        Level.left [] []
        Level.right [] [
            Level.item [] [
                Field.div [] [
                    Control.div [] [ createNewUserButton dispatch ]
                ]
            ]
            Level.item [] [
                Field.div [] [
                    Control.div [] [ toggleFilterDisplayButton dispatch ]
                ]
            ]
        ]
    ]

let updateUserIsBlockedModalConfiguration
    (dispatch: Msg -> unit)
    (fullName: string)
    (request: UpdateUserIsBlockedRequest)
    : ConfirmationModal.Configuration =
    {
        Headline = "Benutzer anpassen"
        Text =
            if request.IsBlocked then
                sprintf "Benutzer '%s' sperren?" fullName
            else
                sprintf "Benutzer '%s' freischalten?" fullName

        OnClose = fun _ -> UserListMsg.CloseModal |> UserList |> dispatch
        OnYes = fun _ -> (fullName, request) |> UserListMsg.UpdateUserIsBlocked |> UserList |> dispatch
        OnNo = fun _ -> UserListMsg.CloseModal |> UserList |> dispatch
    }

let deleteUserModalConfiguration dispatch (id: int) (name: string) : ConfirmationModal.Configuration = {
    Headline = "Benutzer löschen"
    Text = sprintf "Soll der Benutzer '%s' gelöscht werden?" name
    OnClose = fun _ -> UserListMsg.CloseModal |> UserList |> dispatch
    OnYes = fun _ -> (id, name) |> UserListMsg.DeleteUser |> UserList |> dispatch
    OnNo = fun _ -> UserListMsg.CloseModal |> UserList |> dispatch
}


let private updateUserIsBlockedView dispatch (data: IsBlockedModal) =
    let configuration =
        updateUserIsBlockedModalConfiguration dispatch data.User data.Request

    ConfirmationModal.view data.IsRunning configuration

let private modalView dispatch (modal: ModalData) =
    match modal with
    | DeletionModal _ -> div [] []
    | BlockingModal blockingModal -> updateUserIsBlockedView dispatch blockingModal
    | EditModal model -> Forms.User.view dispatch model
    | AddPackageModal model -> Forms.AddPackage.view dispatch model

let private filtersView dispatch (data: Data) =
    Box.box' [ GenericOption.CustomClass "filter_box" ] [
        Columns.columns [] [
            Column.column [ Column.Width(Screen.All, Column.Is4) ] [
                Field.div [] [
                    Label.label [] [ str "Name" ]
                    Control.div [ Control.HasIconLeft ] [
                        Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.Search ] [] ]

                        Input.text [
                            data.NameFilter |> Option.defaultValue "" |> Input.ValueOrDefault
                            Input.OnChange(fun event ->
                                event.Value |> String.toOption |> UserListMsg.NameFilterChanged |> dispatch
                            )
                        ]
                    ]
                ]
            ]
            Column.column [ Column.Width(Screen.All, Column.Is4) ] [
                Field.div [] [
                    Label.label [] [ str "Email Adresse" ]
                    Control.div [ Control.HasIconLeft ] [
                        Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.Search ] [] ]

                        Input.text [
                            data.MailFilter |> Option.defaultValue "" |> Input.ValueOrDefault
                            Input.OnChange(fun event ->
                                event.Value |> String.toOption |> UserListMsg.MailFilterChanged |> dispatch
                            )
                        ]
                    ]
                ]
            ]
            Column.column [ Column.Width(Screen.All, Column.Is4) ] [
                Field.div [] [
                    Label.label [] [ str "SevDesk Kundennummer" ]

                    Control.div [ Control.HasIconLeft ] [
                        Icon.icon [ Icon.Size IsSmall; Icon.IsLeft ] [ Fa.i [ Fa.Solid.Search ] [] ]

                        Input.text [
                            data.CustomerNumberFilter |> Option.defaultValue "" |> Input.ValueOrDefault
                            Input.OnChange(fun event ->
                                event.Value
                                |> String.toOption
                                |> UserListMsg.CustomerNumberFilterChanged
                                |> dispatch
                            )
                        ]
                    ]
                ]
            ]
        ]
    ]

let private dataView dispatch (data: Data) =
    let table = userListToTable dispatch data.Packages data.DisplayedUsers

    let containerContent = [
        Heading.h1 [] [ str "Benutzer Liste" ]
        tableHeader dispatch

        match data.DisplayFilters with
        | true -> filtersView (UserList >> dispatch) data
        | false -> ()

        Table.scrollableTable table

        match data.Modal with
        | Some modal -> modalView dispatch modal
        | None -> ()
    ]

    Container.container
        [
            Container.IsFluid
            Container.CustomClass "content-container"
        ]
        containerContent


let view (model: Model) dispatch = Loadable.view (dataView dispatch) model